import { useEffect } from "react";
import { App, Form, Input, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import type { SupplierDto } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useModalProps } from "../useModalProps";
import { useSaveSupplier, useSuppliers, type SupplierInput } from "../../api/suppliers";
import { useShowRisk } from "../../api/plan";

export function SupplierFormModal({ open, onClose, supplier, onSaved }: { open: boolean; onClose: () => void; supplier?: SupplierDto; onSaved?: (s: SupplierDto) => void }) {
  const { t } = useTranslation();
  const showRisk = useShowRisk();
  const { message } = App.useApp();
  const modalProps = useModalProps(720);
  const [form] = Form.useForm();
  const save = useSaveSupplier();
  const all = useSuppliers();
  const cats = [...new Set((all.data ?? []).map((s) => s.category).filter(Boolean))];

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue(supplier ? { ...supplier } : { risk: "medium", code: `FOR-${String((all.data?.length ?? 0) + 1).padStart(4, "0")}` });
  }, [open, supplier, form, all.data?.length]);

  const submit = async (v: Record<string, string>) => {
    const body: SupplierInput = {
      code: v.code!.trim(),
      name: v.name!.trim(),
      vat: v.vat?.trim() ?? "",
      country: v.country?.trim() ?? "",
      // sensitive-ok: invio del form
      legalAddress: v.legalAddress ?? "",
      // sensitive-ok: invio del form
      contactName: v.contactName ?? "",
      // sensitive-ok: invio del form
      contactEmail: v.contactEmail ?? "",
      // sensitive-ok: invio del form
      contactPhone: v.contactPhone ?? "",
      category: v.category ?? "",
      sector: v.sector ?? "",
      risk: v.risk as SupplierInput["risk"],
    };
    try {
      const s = await save.mutateAsync({ id: supplier?.id, body });
      void message.success(t("saved"));
      onSaved?.(s);
      onClose();
    } catch (e) {
      void message.error(e instanceof ApiError ? e.message : t("required"));
    }
  };

  return (
    <Modal open={open} onCancel={onClose} onOk={() => form.submit()} okText={t("save")} cancelText={t("cancel")} confirmLoading={save.isPending} {...modalProps} title={supplier ? t("editSupplier") : t("newSupplier")} destroyOnHidden>
      <Form form={form} layout="vertical" onFinish={submit} requiredMark="optional">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", columnGap: 16 }}>
          <Form.Item name="code" label={t("sapCode")} rules={[{ required: true, message: t("required") }]}>
            <Input />
          </Form.Item>
          <Form.Item name="vat" label={t("vat")}>
            <Input placeholder="IT01234567890" />
          </Form.Item>
          <Form.Item name="country" label={t("country")}>
            <Input />
          </Form.Item>
        </div>
        <Form.Item name="name" label={t("name")} rules={[{ required: true, message: t("required") }]}>
          <Input />
        </Form.Item>
        <Form.Item name="legalAddress" label={t("legalAddress")}>
          <Input data-sensitive />
        </Form.Item>
        <Form.Item name="sites" label={t("sites")} extra={t("sitesHint")}>
          <Input.TextArea data-sensitive rows={3} />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(220px, 100%), 1fr))", columnGap: 16 }}>
          <Form.Item name="contactName" label={t("contact")}>
            <Input data-sensitive />
          </Form.Item>
          <Form.Item name="contactEmail" label={t("email")} rules={[{ type: "email", message: t("a_emailInvalid") }]}>
            <Input data-sensitive />
          </Form.Item>
          <Form.Item name="contactPhone" label={t("phone")}>
            <Input data-sensitive />
          </Form.Item>
          <Form.Item name="category" label={t("category")}>
            <Input list="ap-cats" />
          </Form.Item>
          <Form.Item name="sector" label={t("sector")}>
            <Input />
          </Form.Item>
          <Form.Item name="risk" label={t("risk")} extra={supplier ? t("m_riskAdminOnly") : undefined} hidden={!showRisk}>
            <Select disabled={!!supplier} options={["low", "medium", "high"].map((r) => ({ value: r, label: t(`risk_${r}`) }))} />
          </Form.Item>
        </div>
      </Form>
      <datalist id="ap-cats">
        {cats.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </Modal>
  );
}
